import { registerLocaleData } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi
} from '@angular/common/http';
import localeBr from '@angular/common/locales/pt';
import {
  APP_ID,
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  importProvidersFrom,
  LOCALE_ID,
  provideZoneChangeDetection
} from '@angular/core';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter, Routes } from '@angular/router';

import { PDFJS_LICENSE_TOKEN, ValidationBodyInterceptor } from '@aionzdev/design-system';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { OverlayContainer } from '@angular/cdk/overlay';
import { ReactiveFormsModule } from '@angular/forms';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDatetimepickerModule } from '@mat-datetimepicker/core';
import { MatMomentDatetimeModule } from '@mat-datetimepicker/moment';
import { ScrollToModule } from '@paulvmoreau/ngx-scroll-to';
import { provideTranslateLoader, provideTranslateService } from '@ngx-translate/core';
import moment from 'moment';
import 'moment/locale/pt-br';
import { provideNgxMask } from 'ngx-mask';
import { AppSettings } from './app.settings';
import { AionzTranslateLoader } from './core/i18n/aionz-translate-loader';
import { AuthGuard } from './core/guard/auth.guard';
import { RedirectLandingGuard } from './core/guard/redirect-landing.guard';
import { TokenInterceptor } from './core/interceptors/token.interceptor';
import { MenuComponent } from './pages/gerenciadores/menu/menu.component';
import { NavService } from './services/nav.service';
import { CustomOverlayContainer } from './theme/utils/custom-overlay-container';

registerLocaleData(localeBr, 'pt');
moment.locale('pt-br');

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    loadChildren: () => import('./pages/home/home.module').then(m => m.HomeModule)
  },
  {
    path: 'login',
    loadChildren: () => import('./pages/login/login.module').then(m => m.LoginModule)
  },
  {
    path: 'politica-de-privacidade',
    loadChildren: () =>
      import('./pages/politica-de-privacidade/politica-de-privacidade.module').then(
        m => m.PoliticaDePrivacidadeModule
      )
  },
  {
    path: 'termos-de-uso',
    loadChildren: () =>
      import('./pages/termos-de-uso/termos-de-uso.module').then(m => m.TermosDeUsoModule)
  },
  {
    path: 'admin',
    canActivate: [AuthGuard],
    component: MenuComponent,
    loadChildren: () =>
      import('./pages/gerenciadores/gerenciadores.module').then(m => m.GerenciadoresModule)
  },
  {
    path: 'confirmacao/:token-email',
    loadChildren: () =>
      import('./pages/confirmacao-dupla/confirmacao-dupla.module').then(
        m => m.ConfirmacaoDuplaModule
      )
  },
  {
    path: 'unsub-mail/:mail',
    loadChildren: () => import('./pages/unsub-mail/unsub-mail.module').then(m => m.UnsubMailModule)
  },
  {
    path: 'landing/:refurl',
    canActivate: [RedirectLandingGuard],
    loadChildren: () =>
      import('./pages/landing-page/landing-page.module').then(m => m.LandingPageModule)
  },
  {
    path: ':refurl',
    loadChildren: () =>
      import('./pages/landing-page/landing-page.module').then(m => m.LandingPageModule)
  }
];

const DEFAULT_PDFJS_LICENSE = 'EdFyE4N4uwLN2pix8FNK';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideTranslateService({
      loader: provideTranslateLoader(AionzTranslateLoader),
      lang: 'pt-BR',
      fallbackLang: 'pt-BR'
    }),
    provideRouter(routes),
    provideClientHydration(withEventReplay()),
    provideAnimationsAsync(),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideNgxMask(),
    importProvidersFrom(
      ReactiveFormsModule,
      DragDropModule,
      MatChipsModule,
      MatIconModule,
      ScrollToModule.forRoot(),
      MatBottomSheetModule,
      MatDialogModule,
      MatSnackBarModule,
      MatDatepickerModule,
      MatDatetimepickerModule,
      MatMomentDatetimeModule
    ),
    { provide: APP_ID, useValue: 'serverApp' },
    AppSettings,
    NavService,
    { provide: OverlayContainer, useClass: CustomOverlayContainer },
    { provide: LOCALE_ID, useValue: 'pt' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'BRL' },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    { provide: HTTP_INTERCEPTORS, useClass: ValidationBodyInterceptor, multi: true },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        panelClass: 'ebarn-dialog',
        backdropClass: 'ebarn-background-modal',
        hasBackdrop: true
      }
    },
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: { duration: 5000 }
    },
    { provide: PDFJS_LICENSE_TOKEN, useValue: DEFAULT_PDFJS_LICENSE },
    MatDatepickerModule,
    MatMomentDatetimeModule
  ]
};
